// Legal policy pages content — served at /policies/<slug>
// Owner: Akhilesh Kumar Suman (FilmmakerAkki) · screenextfilms@gmail.com · India

export const POLICIES = {
  "terms-and-conditions": {
    title: "Terms & Conditions",
    updated: "September 2026",
    intro: "These Terms govern your use of filmmakerakki (the \"Website\") operated by Akhilesh Kumar Suman (\"FilmmakerAkki\", \"we\", \"us\"). By using this Website or purchasing any product or service, you agree to these Terms.",
    sections: [
      {
        h: "1. Website Usage",
        p: "The Website showcases filmmaking services, workshops, digital products, and stock footage. You agree to use the Website only for lawful purposes. You must not misuse the Website, attempt unauthorized access, or interfere with its operation. Content on this Website is for your personal, non-commercial viewing unless explicitly licensed.",
      },
      {
        h: "2. Digital Product Purchases",
        p: "Digital products (LUTs, presets, guides, checklists, eBooks) are licensed, not sold. A purchase grants you a non-exclusive, non-transferable license for personal and commercial project use. You may not resell, redistribute, or share the files or your download links. Prices are listed in INR and may change without notice; the price at the time of your purchase applies to you.",
      },
      {
        h: "3. Workshops & Consultations",
        p: "Workshop seats and 1:1 consultations are confirmed only after successful payment. Batch dates, locations, and inclusions are communicated in advance. We may reschedule a workshop due to weather, permissions, or unforeseen circumstances — in such cases you may choose a full refund or a free transfer to the next batch. See our Refund Policy for details.",
      },
      {
        h: "4. User Responsibilities",
        p: "You are responsible for providing accurate contact details (name, email, phone) during purchase or booking. Delivery of digital products and booking confirmations depends on the email you provide. You must be 18 years or older, or have guardian consent, to make a purchase.",
      },
      {
        h: "5. Intellectual Property",
        p: "All content on this Website — films, footage, photographs, text, and branding — is the property of Akhilesh Kumar Suman unless stated otherwise. See our Copyright Policy for ownership and enforcement details.",
      },
      {
        h: "6. Limitation of Liability",
        p: "Services and products are provided \"as is\" without warranties of any kind. To the maximum extent permitted by law, we are not liable for indirect or consequential damages arising from the use of our products or services.",
      },
      {
        h: "7. Governing Law",
        p: "These Terms are governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts of Uttar Pradesh, India.",
      },
      {
        h: "8. Contact",
        p: "For any questions about these Terms, contact: screenextfilms@gmail.com.",
      },
    ],
  },
  "privacy-policy": {
    title: "Privacy Policy",
    updated: "September 2026",
    intro: "This Privacy Policy explains what information FilmmakerAkki (Akhilesh Kumar Suman) collects, why, and your rights over it — in line with the Information Technology Act, 2000 and applicable Indian data protection rules.",
    sections: [
      {
        h: "1. Data We Collect",
        p: "When you book a consultation or workshop, purchase a product, or join a waitlist, we collect: name, email address, phone number, and payment-related identifiers (Razorpay order/payment IDs). We do NOT store your card, UPI, or banking details — payments are processed securely by Razorpay.",
      },
      {
        h: "2. How We Use Your Data",
        p: "Your data is used to: confirm bookings and deliver digital products, send payment confirmations and workshop details, notify you about launches you subscribed to (e.g. eBook waitlist), and respond to your enquiries. We may send service emails related to your purchase.",
      },
      {
        h: "3. What We Never Do",
        p: "We do not sell, rent, or trade your personal data to third parties. We do not send spam. Marketing emails are sent only to users who explicitly joined a list, and you can opt out anytime by replying to any email.",
      },
      {
        h: "4. Third-Party Processors",
        p: "We use Razorpay (payments) and a transactional email service (delivery). These processors handle your data only to complete the service you requested, under their own privacy terms.",
      },
      {
        h: "5. Data Retention & Security",
        p: "Booking and order records are retained for accounting and legal compliance. We apply reasonable technical and organizational safeguards to protect your data, per the Information Technology (Reasonable Security Practices) Rules.",
      },
      {
        h: "6. Your Rights",
        p: "You may request access, correction, or deletion of your personal data at any time by emailing screenextfilms@gmail.com. We respond to such requests within 30 days.",
      },
      {
        h: "7. Cookies",
        p: "The Website uses minimal cookies/local storage only for essential functionality (e.g. keeping you logged in). No advertising trackers are used.",
      },
      {
        h: "8. Grievance Officer",
        p: "As required under the IT Act, 2000: Grievance Officer — Akhilesh Kumar Suman, screenextfilms@gmail.com. Complaints are addressed within 30 days.",
      },
    ],
  },
  "shipping-delivery-policy": {
    title: "Shipping & Delivery Policy",
    updated: "September 2026",
    intro: "All products sold on this Website are 100% digital. There is no physical shipping involved.",
    sections: [
      {
        h: "1. Instant Digital Delivery",
        p: "After a successful payment, your purchase is confirmed instantly on-screen and via email. Download links for digital products (LUTs, presets, guides, checklists, eBooks) are delivered to the email address provided at checkout — typically within a few hours, and in most cases much faster.",
      },
      {
        h: "2. Delivery Issues",
        p: "If you do not receive your download link within 24 hours of payment, please check your spam/promotions folder first, then contact screenextfilms@gmail.com with your Order ID and payment reference. We will re-send the link promptly.",
      },
      {
        h: "3. Workshops & Consultations",
        p: "Workshop bookings and consultations involve no shipping. Joining details, schedules, and preparation material are shared via email and WhatsApp before the session date.",
      },
      {
        h: "4. No Physical Goods",
        p: "Nothing purchased on this Website will be couriered. Any merchandise or physical goods launched in the future will carry a separate shipping policy at that time.",
      },
    ],
  },
  "refund-policy": {
    title: "Refund & Cancellation Policy",
    updated: "September 2026",
    intro: "We keep refunds simple and fair. This policy covers digital products, stock footage, workshops, and consultations sold on this Website.",
    sections: [
      {
        h: "1. Digital Products & Stock Footage",
        p: "Due to the instantly accessible nature of digital goods, all sales are final and non-refundable once payment is completed. Exceptions: (a) technical failure — the file is corrupted or will not download and we cannot resolve it within 72 hours; (b) duplicate/accidental double payment — the extra charge is refunded in full. Approved refunds are processed to the original payment method within 5–7 business days via Razorpay.",
      },
      {
        h: "2. Workshop Cancellations (by you)",
        p: "Cancel 14+ days before your batch: full refund. Cancel 7–13 days before: 50% refund or a free transfer to the next batch. Cancel within 7 days of the batch: non-refundable, but your advance can be adjusted against a future batch once, subject to seat availability.",
      },
      {
        h: "3. Workshop Cancellations (by us)",
        p: "If we cancel or reschedule a workshop (weather, permissions, low enrolment, or unforeseen circumstances), you choose: a 100% full refund or a priority seat in the next batch at no extra cost.",
      },
      {
        h: "4. Consultation Calls",
        p: "1:1 consultations can be rescheduled once with 12+ hours notice. No-shows are non-refundable. If we miss the scheduled call, you get a full refund or a free reschedule — your choice.",
      },
      {
        h: "5. How to Request",
        p: "Email screenextfilms@gmail.com with your Booking/Order ID and payment reference. We respond within 48 hours.",
      },
    ],
  },
  "disclaimer": {
    title: "Disclaimer",
    updated: "September 2026",
    intro: "Please read this Disclaimer carefully before purchasing or using any content from this Website.",
    sections: [
      {
        h: "1. Licensed Use Only",
        p: "Stock footage and digital content sold on this Website is for licensed use only. Your purchase grants usage rights for your own projects (personal or commercial). Reselling, redistributing, sublicensing, or sharing the raw files — free or paid — is strictly prohibited.",
      },
      {
        h: "2. No Professional Advice",
        p: "Guides, settings, and checklists reflect real field experience but are educational resources, not certified training or legal/safety advice. Drone operations are subject to DGCA regulations — you are responsible for flying legally in your region.",
      },
      {
        h: "3. Results May Vary",
        p: "Creative outcomes from LUTs, presets, and workflows depend on your footage, equipment, and skill. We do not guarantee identical results to samples shown.",
      },
      {
        h: "4. External Links",
        p: "The Website may link to third-party platforms (YouTube, Instagram, IMDb). We are not responsible for their content or policies.",
      },
      {
        h: "5. Accuracy",
        p: "We work to keep information accurate, but the Website may contain errors or omissions. We may update content and prices at any time without notice.",
      },
    ],
  },
  "copyright-policy": {
    title: "Copyright Policy",
    updated: "September 2026",
    intro: "All creative work on this Website is protected under the Copyright Act, 1957 (India) and international copyright treaties.",
    sections: [
      {
        h: "1. Ownership",
        p: "All aerial footage, films, photographs, digital products, text, and brand assets on this Website are owned by Akhilesh Kumar Suman (FilmmakerAkki), unless a third-party credit is explicitly shown. Client-commissioned works remain subject to their respective agreements.",
      },
      {
        h: "2. What Purchase Gives You",
        p: "Buying stock footage or a digital product grants you a non-exclusive license to use it in your projects. Ownership (copyright) always remains with Akhilesh Kumar Suman. You may not claim the content as your own, register it in content-ID systems, or include it in any stock/library collection.",
      },
      {
        h: "3. Prohibited Use",
        p: "Unauthorized use — including ripping, re-uploading, reselling, or using Website content without a valid license — is copyright infringement. Such use will be subject to takedown action and legal remedies under applicable law.",
      },
      {
        h: "4. Takedown / Licensing Requests",
        p: "For licensing enquiries beyond standard purchase, commercial syndication, or to report infringement, contact: screenextfilms@gmail.com. We respond within 48 hours.",
      },
      {
        h: "5. Trademarks",
        p: "DJI, Razorpay, and other brand names mentioned belong to their respective owners and are used for identification only. FilmmakerAkki is the personal brand of Akhilesh Kumar Suman.",
      },
    ],
  },
};

export const POLICY_LIST = [
  { slug: "terms-and-conditions", label: "Terms & Conditions" },
  { slug: "privacy-policy", label: "Privacy Policy" },
  { slug: "shipping-delivery-policy", label: "Shipping & Delivery" },
  { slug: "refund-policy", label: "Refund Policy" },
  { slug: "disclaimer", label: "Disclaimer" },
  { slug: "copyright-policy", label: "Copyright Policy" },
];
